/**
 * Scenes and team crests for the sports and event templates of the gallery: drawn in SVG,
 * so they ship with the code and need no stock photos. Uploaded as PNG when used.
 */

const scene = (w: number, h: number, defs: string, body: string) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><defs>${defs}</defs>${body}</svg>`;

/** Deterministic pseudo-random numbers so the art is the same on every build. */
function rng(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807) % 2147483647;
    return s / 2147483647;
  };
}

function bokeh(n: number, w: number, h: number, seed: number, colors: string[], maxR = 26) {
  const r = rng(seed);
  let out = '';
  for (let i = 0; i < n; i++) {
    const c = colors[Math.floor(r() * colors.length)];
    out += `<circle cx="${Math.round(r() * w)}" cy="${Math.round(r() * h)}" r="${Math.round(4 + r() * maxR)}" fill="${c}" opacity="${(0.15 + r() * 0.45).toFixed(2)}"/>`;
  }
  return out;
}

function crowd(y: number, w: number, rows: number, seed: number, color: string) {
  const r = rng(seed);
  let out = '';
  for (let row = 0; row < rows; row++) {
    const yy = y + row * 34;
    for (let x = -10; x < w + 20; x += 22 + r() * 10) {
      const hr = 11 + r() * 5;
      out += `<circle cx="${x.toFixed(0)}" cy="${(yy + r() * 8).toFixed(0)}" r="${hr.toFixed(1)}" fill="${color}"/>`;
      out += `<rect x="${(x - hr - 4).toFixed(0)}" y="${(yy + hr).toFixed(0)}" width="${(hr * 2 + 8).toFixed(0)}" height="40" rx="12" fill="${color}"/>`;
    }
  }
  return out;
}

/** Night stadium: floodlights, light cones, stands with fans, a striped pitch. */
export const stadium = (() => {
  const W = 1920;
  const H = 1080;
  const defs = `<linearGradient id="sky" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#020617"/><stop offset="0.55" stop-color="#0B1E3F"/><stop offset="1" stop-color="#123A5E"/></linearGradient>
<radialGradient id="flood" cx="0.5" cy="0" r="1"><stop offset="0" stop-color="#FFFFFF" stop-opacity="0.85"/><stop offset="0.35" stop-color="#BFE3FF" stop-opacity="0.25"/><stop offset="1" stop-color="#BFE3FF" stop-opacity="0"/></radialGradient>
<linearGradient id="pitch" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#14532D"/><stop offset="1" stop-color="#22C55E"/></linearGradient>
<radialGradient id="haze" cx="0.5" cy="0.62" r="0.6"><stop offset="0" stop-color="#7DD3FC" stop-opacity="0.35"/><stop offset="1" stop-color="#7DD3FC" stop-opacity="0"/></radialGradient>`;
  const lights = [260, 760, 1160, 1660]
    .map(
      (x) =>
        `<path d="M${x - 40} 150 L ${x + 40} 150 L ${x + 420} 900 L ${x - 420} 900 Z" fill="url(#flood)" opacity="0.55"/>
<rect x="${x - 70}" y="110" width="140" height="44" rx="8" fill="#E2E8F0"/>
${[0, 1, 2, 3].map((i) => `<circle cx="${x - 50 + i * 33}" cy="132" r="13" fill="#FFFFFF"/>`).join('')}
<circle cx="${x}" cy="132" r="120" fill="#FFFFFF" opacity="0.12"/>`,
    )
    .join('');
  let stripes = '';
  for (let i = 0; i < 12; i++) {
    const y0 = 760 + i * 28 + i * i * 1.6;
    stripes += `<rect x="0" y="${y0.toFixed(0)}" width="${W}" height="${(14 + i * 2).toFixed(0)}" fill="#FFFFFF" opacity="${i % 2 ? 0 : 0.06}"/>`;
  }
  const body = `<rect width="${W}" height="${H}" fill="url(#sky)"/>
${bokeh(70, W, 520, 7, ['#FFFFFF', '#93C5FD', '#FDE68A'], 6)}
<path d="M0 560 C 400 470 1520 470 ${W} 560 L ${W} 780 L 0 780 Z" fill="#0F172A"/>
${crowd(540, W, 6, 11, '#1E293B')}
<path d="M0 560 C 400 470 1520 470 ${W} 560" stroke="#38BDF8" stroke-width="6" fill="none" opacity="0.7"/>
<rect x="0" y="740" width="${W}" height="40" fill="#0EA5E9" opacity="0.85"/>
<rect x="0" y="752" width="${W}" height="16" fill="#FFFFFF" opacity="0.25"/>
<rect x="0" y="780" width="${W}" height="${H - 780}" fill="url(#pitch)"/>
${stripes}
<path d="M120 1080 L 560 800 L 1360 800 L 1800 1080" stroke="#FFFFFF" stroke-width="5" fill="none" opacity="0.55"/>
<ellipse cx="960" cy="930" rx="260" ry="70" stroke="#FFFFFF" stroke-width="5" fill="none" opacity="0.5"/>
<rect width="${W}" height="${H}" fill="url(#haze)"/>
${lights}`;
  return scene(W, H, defs, body);
})();

/** Basketball arena: glossy parquet, spotlights, scoreboard glow. */
export const arena = (() => {
  const W = 1200;
  const H = 1500;
  const defs = `<linearGradient id="dark" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0C0A09"/><stop offset="1" stop-color="#292524"/></linearGradient>
<linearGradient id="wood" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#92400E"/><stop offset="1" stop-color="#F59E0B"/></linearGradient>
<radialGradient id="spot" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#FDBA74" stop-opacity="0.7"/><stop offset="1" stop-color="#FDBA74" stop-opacity="0"/></radialGradient>`;
  let planks = '';
  for (let i = 0; i < 26; i++) {
    const x = -600 + i * 92;
    planks += `<path d="M${600 + (x - 600) * 0.35} 900 L ${x} ${H}" stroke="#78350F" stroke-width="3" opacity="0.35"/>`;
  }
  const body = `<rect width="${W}" height="${H}" fill="url(#dark)"/>
${bokeh(60, W, 760, 23, ['#FDBA74', '#FFFFFF', '#F97316'], 10)}
${crowd(640, W, 6, 5, '#1C1917')}
<path d="M0 900 L ${W} 900 L ${W} ${H} L 0 ${H} Z" fill="url(#wood)"/>
${planks}
<ellipse cx="600" cy="1180" rx="520" ry="150" fill="url(#spot)"/>
<ellipse cx="600" cy="1180" rx="240" ry="70" stroke="#FFFFFF" stroke-width="6" fill="none" opacity="0.8"/>
<path d="M0 1040 L ${W} 1040" stroke="#FFFFFF" stroke-width="6" opacity="0.7"/>
<rect x="430" y="150" width="340" height="150" rx="18" fill="#0A0A0A" stroke="#F97316" stroke-width="6"/>
<circle cx="600" cy="225" r="200" fill="#F97316" opacity="0.12"/>
<rect x="0" y="0" width="${W}" height="${H}" fill="url(#spot)" opacity="0.15"/>`;
  return scene(W, H, defs, body);
})();

/** Concert stage: light beams, haze, crowd hands. */
export const concert = (() => {
  const W = 1200;
  const H = 1500;
  const defs = `<linearGradient id="night" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1E0B3B"/><stop offset="1" stop-color="#4C1D95"/></linearGradient>
<linearGradient id="beam" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#FFFFFF" stop-opacity="0.7"/><stop offset="1" stop-color="#F0ABFC" stop-opacity="0"/></linearGradient>`;
  const beams = [
    [150, -20, 0, 1100],
    [420, -20, 250, 1200],
    [780, -20, 900, 1200],
    [1050, -20, 1200, 1100],
    [600, -20, 520, 1250],
  ]
    .map(
      ([x1, y1, x2, y2]) =>
        `<path d="M${x1 - 18} ${y1} L ${x1 + 18} ${y1} L ${x2 + 160} ${y2} L ${x2 - 160} ${y2} Z" fill="url(#beam)" opacity="0.5"/>`,
    )
    .join('');
  const r = rng(41);
  let hands = '';
  for (let x = -20; x < W; x += 60 + r() * 50) {
    const top = 1180 + r() * 120;
    hands += `<path d="M${x} ${H} L ${x + 8} ${top + 30} C ${x + 6} ${top} ${x + 30} ${top} ${x + 30} ${top + 25} L ${x + 34} ${H} Z" fill="#0B0414"/>`;
  }
  const body = `<rect width="${W}" height="${H}" fill="url(#night)"/>
${beams}
${bokeh(90, W, 1100, 3, ['#F0ABFC', '#FFFFFF', '#67E8F9', '#FDE68A'], 18)}
<rect x="0" y="1060" width="${W}" height="60" fill="#0B0414"/>
${crowd(1120, W, 4, 9, '#0B0414')}
${hands}`;
  return scene(W, H, defs, body);
})();

/** A team crest: shield or round badge, with stripes, a star and initials. */
function crest(
  shape: 'shield' | 'round',
  primary: string,
  secondary: string,
  initials: string,
  pattern: 'stripes' | 'chevron' | 'ball' | 'half',
) {
  const S = 600;
  const outline =
    shape === 'shield'
      ? 'M300 30 L 540 100 L 540 300 C 540 440 430 530 300 575 C 170 530 60 440 60 300 L 60 100 Z'
      : 'M300 30 A 270 270 0 1 1 299.9 30 Z';
  const inner =
    shape === 'shield'
      ? 'M300 70 L 505 130 L 505 300 C 505 420 410 495 300 535 C 190 495 95 420 95 300 L 95 130 Z'
      : 'M300 70 A 230 230 0 1 1 299.9 70 Z';
  const fill =
    pattern === 'stripes'
      ? [0, 1, 2, 3, 4, 5]
          .map(
            (i) => `<rect x="${100 + i * 70}" y="0" width="35" height="${S}" fill="${secondary}"/>`,
          )
          .join('')
      : pattern === 'chevron'
        ? `<path d="M60 180 L 300 360 L 540 180 L 540 280 L 300 460 L 60 280 Z" fill="${secondary}"/>`
        : pattern === 'half'
          ? `<rect x="300" y="0" width="300" height="${S}" fill="${secondary}"/>`
          : `<circle cx="300" cy="330" r="120" fill="${secondary}"/><path d="M300 250 L 345 285 L 330 340 L 270 340 L 255 285 Z" fill="${primary}"/>`;
  return scene(
    S,
    S,
    `<clipPath id="in"><path d="${inner}"/></clipPath><linearGradient id="shine" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#FFFFFF" stop-opacity="0.35"/><stop offset="0.5" stop-color="#FFFFFF" stop-opacity="0"/></linearGradient>`,
    `<path d="${outline}" fill="${secondary}" stroke="#0F172A" stroke-width="10"/>
<g clip-path="url(#in)"><rect width="${S}" height="${S}" fill="${primary}"/>${fill}<rect width="${S}" height="${S}" fill="url(#shine)"/></g>
<path d="${inner}" fill="none" stroke="#0F172A" stroke-width="6"/>
<path d="M300 110 L 314 150 L 356 150 L 322 174 L 335 214 L 300 190 L 265 214 L 278 174 L 244 150 L 286 150 Z" fill="#FDE047" stroke="#0F172A" stroke-width="4"/>
<rect x="170" y="${shape === 'shield' ? 395 : 410}" width="260" height="78" rx="16" fill="#0F172A"/>
<text x="300" y="${shape === 'shield' ? 450 : 465}" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="900" font-size="52" letter-spacing="6" fill="#FFFFFF">${initials}</text>`,
  );
}

export const crestRed = crest('shield', '#DC2626', '#FFFFFF', 'FCN', 'stripes');
export const crestBlue = crest('round', '#1D4ED8', '#FACC15', 'RVU', 'ball');
export const crestGreen = crest('shield', '#15803D', '#F8FAFC', 'GRN', 'chevron');
export const crestBlack = crest('round', '#111827', '#F59E0B', 'IWF', 'half');
